import React from 'react';
import { motion } from 'framer-motion';
import { FileText, MousePointer2, Check } from 'lucide-react';
import { useStepCycle } from '../../hooks/useStepCycle';
import { EASE_IN_OUT, EASE_OUT } from '../../utils/motion';
const bars = [40, 65, 50, 80, 60, 90];
function blockClass(filled: boolean) {
  return `rounded-md border transition-colors duration-300 ${filled ? 'bg-primary/10 border-transparent' : 'border-dashed border-primary/25 bg-transparent'}`;
}
export function PrototypeIllustration() {
  const phase = useStepCycle(6, 1100);
  return <div className="absolute inset-0 p-6 xl:p-8" data-id="element-95" data-mp="4zi2xpqs19wf">
      <div className="relative h-full ml-12 xl:ml-20 bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col" data-id="element-96" data-mp="3w485jl6cq7km">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100" data-id="element-97" data-mp="3r5wytjbcsd6m">
          <span className="w-2.5 h-2.5 rounded-full bg-gray-200" data-id="element-98" data-mp="1uash8uxql4st" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-200" data-id="element-99" data-mp="1pch6mhzmaq9j" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-200" data-id="element-100" data-mp="1pb61oeufrcgu" />
          <span className="ml-3 h-2 w-32 rounded-full bg-bg-alt" data-id="element-101" data-mp="2h2gpu63rmbkz" />
          <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full transition-colors duration-200 ${phase >= 4 ? 'bg-accent text-primary' : 'bg-bg-alt text-text-muted'}`} data-id="element-102" data-mp="2m3o0ccxalovc">
            {phase >= 4 ? 'Prototyp' : 'Entwurf'}
          </span>
        </div>

        <div className="flex-1 flex gap-3 p-4 min-h-0" data-id="element-103" data-mp="2zvuc7mtex9oz">
          <div className={`w-1/5 p-2 flex flex-col gap-2 ${blockClass(phase >= 1)}`} data-id="element-104" data-mp="34tcq7rmaef3u">
            {[0, 1, 2, 3].map(i => <span key={i} className={`h-2 rounded-full transition-colors duration-300 ${phase >= 1 ? 'bg-primary/30' : 'bg-primary/10'}`} data-id="element-105" data-mp="2q3onqn2ddaej" />)}
          </div>
          <div className="flex-1 flex flex-col gap-3 min-h-0" data-id="element-106" data-mp="3irkfddd30c4g">
            <div className={`h-8 shrink-0 ${blockClass(phase >= 1)}`} data-id="element-107" data-mp="3nlopg5hn909r" />
            <div className="grid grid-cols-3 gap-3 shrink-0" data-id="element-108" data-mp="1aydlos5ng0b">
              {[0, 1, 2].map(i => <div key={i} className={`h-14 rounded-md border transition-colors duration-300 ${phase >= 2 ? i === 0 ? 'bg-accent/40 border-transparent' : 'bg-primary/10 border-transparent' : 'border-dashed border-primary/25'}`} data-id="element-109" data-mp="3sgrg2hn15qgk" />)}
            </div>
            <div className={`flex-1 flex items-end gap-2 p-3 min-h-0 ${blockClass(phase >= 3)}`} data-id="element-110" data-mp="39of4d4ahlctr">
              {bars.map((h, i) => <motion.div key={i} className="flex-1 rounded-t bg-primary" initial={false} animate={{
              height: phase >= 3 ? `${h}%` : '6%',
              opacity: phase >= 3 ? 1 : 0.15
            }} transition={{
              duration: 0.3,
              ease: EASE_OUT,
              delay: phase >= 3 ? i * 0.04 : 0
            }} data-id="element-111" data-mp="30qd6gxm1v0ap" data-mpi={i} />)}
            </div>
          </div>
        </div>

        <motion.div className="absolute pointer-events-none" initial={false} animate={{
        left: phase >= 4 ? '34%' : '82%',
        top: phase >= 4 ? '34%' : '82%',
        opacity: phase >= 3 ? 1 : 0
      }} transition={{
        duration: 0.45,
        ease: EASE_IN_OUT
      }} data-id="element-112" data-mp="1g5on6hr0p7ul">
          <motion.span className="absolute -left-3 -top-3 w-6 h-6 rounded-full border-2 border-accent" initial={false} animate={phase === 4 ? {
          scale: [0.6, 1.6],
          opacity: [1, 0]
        } : {
          scale: 0.6,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT,
          delay: 0.45
        }} data-id="element-113" data-mp="wja1ih000rfn" />
          <MousePointer2 className="w-6 h-6 text-primary fill-primary" data-id="element-114" data-mp="3luqkwlwsip7v" />
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-6 w-44 bg-white rounded-xl shadow-lg p-4" data-id="element-115" data-mp="3gwfcmfm58mrr">
        <div className="flex items-center gap-2 mb-3" data-id="element-116" data-mp="3vkjmqv74rpyx">
          <FileText className="w-5 h-5 text-primary" data-id="element-117" data-mp="3vm4giilmf4vq" />
          <span className="text-sm font-bold text-primary" data-id="element-118" data-mp="3qtjonmm9z1uu">Angebot</span>
          <span className="ml-auto w-5 h-5 rounded-full bg-accent flex items-center justify-center" data-id="element-119" data-mp="8g3qwen69mqn">
            <Check className="w-3 h-3 text-primary" strokeWidth={3} data-id="element-120" data-mp="3h0xxskgv6z1" />
          </span>
        </div>
        <div className="space-y-1.5" data-id="element-121" data-mp="ie9qgpliagsc">
          <span className="block h-1.5 w-full rounded-full bg-bg-alt" data-id="element-122" data-mp="id8emiwo1q9k" />
          <span className="block h-1.5 w-4/5 rounded-full bg-bg-alt" data-id="element-123" data-mp="dfgex2ps4zut" />
          <span className="block h-1.5 w-3/5 rounded-full bg-bg-alt" data-id="element-124" data-mp="dd40gzj8d7mi" />
        </div>
      </div>
    </div>;
}